import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import Container from '@mui/material/Container';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';

import AppInput from 'shared/AppInput';
import LocationDropdowns from './components/LocationDropdowns';

import { useFormik } from 'formik';
import axios from 'utils/axios-rails';

import useMuiNotification, { AppNotification } from '@hooks/useMuiNotification';

const propTypes = {
  application: PropTypes.shape({
    id: PropTypes.number.isRequired,
    first_name: PropTypes.string.isRequired,
    last_name: PropTypes.string.isRequired,
    created_at: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    phone: PropTypes.string.isRequired,
    company_name: PropTypes.string.isRequired,
    national_id: PropTypes.string.isRequired,
    province: PropTypes.string,
    canton: PropTypes.string,
    district: PropTypes.string,
    zip: PropTypes.string,
    address: PropTypes.string,
    sector: PropTypes.string,
    company_department: PropTypes.string,
    referral: PropTypes.string,
    employees_count: PropTypes.string,
    brightpearl_contact_owner_id: PropTypes.number,
    shopify_id: PropTypes.string,
    bright_pearl_id: PropTypes.string,
    sync_error: PropTypes.string, // Last Brightpearl error, cleared once the push succeeds
  }).isRequired,

  editable: PropTypes.bool.isRequired,
  contact_owner: PropTypes.string, // from API
  contact_owners: PropTypes.array.isRequired,

  temporalTranslations: PropTypes.shape({
    createdAtHuman: PropTypes.string.isRequired,
    statusHuman: PropTypes.string.isRequired,
    companyDepartmentHuman: PropTypes.string, // Legacy records don't have this field
    referralHuman: PropTypes.string
  }).isRequired,

  indexURL: PropTypes.string.isRequired,
  shopifyURL: PropTypes.string,
  approveURL: PropTypes.string.isRequired,
  rejectURL: PropTypes.string.isRequired,
  updateURL: PropTypes.string.isRequired,

  approvalMessage: PropTypes.string,
}

const defaultProps = {

}

// Label + value row, replacing the Bootstrap definition-list (dt.col-sm-3 / dd.col-sm-9 + border-bottom).
const Field = ({ label, children }) => (
  <>
    <Grid item xs={12} sm={3}><Typography component="span" sx={{ fontWeight: 700 }}>{ label }</Typography></Grid>
    <Grid item xs={12} sm={9}>{ children }</Grid>
    <Grid item xs={12}><Divider /></Grid>
  </>
);

const BusinessApplicationShow = (props) => {
  const [sendNotification, notificationProps] = useMuiNotification();

  const application = props.application;
  const temporalTranslations = props.temporalTranslations;

  const shopify_published = !!application.shopify_id;
  const bp_published = !!application.bright_pearl_id;

  useEffect(() => {
    if(!!props.approvalMessage){
      sendNotification({ message: props.approvalMessage, severity: "info" });
    }
  }, []);

  const handleSave = async (values) => {
    console.log("Sending save request", values);
    try {
      const response = await axios.put(props.updateURL, {
        business_application: values
      });
      sendNotification({ message: response.data.message, severity: "success" });
    }
    catch (error) {
      sendNotification({ message: "Ocurrio un error.", severity: "error" });
      if(error.response && error.response.data){
        formik.setErrors(error.response.data);
      }
    }
    formik.setSubmitting(false);
  }

  const formik = useFormik({
    initialValues: props.application,
    onSubmit: handleSave
  });

  const csrfToken = document.querySelector("meta[name=csrf-token]").content;
  const values = formik.values;

  return (
    <Container sx={{ mt: 2 }}>
      <AppNotification {...notificationProps} />

      <Card>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton href={props.indexURL} size="small" sx={{ color: "#000" }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" sx={{ ml: 1.5 }}>
              Aplicación de: <strong>{ application.name }</strong>
            </Typography>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            Recibida el { temporalTranslations.createdAtHuman }
          </Typography>

          <Box sx={{ mt: 1, mb: 1, display: "flex", gap: 1 }}>
            <Chip label={ temporalTranslations.statusHuman } color="info" size="small" />
            <Chip label={ temporalTranslations.countryHuman } color="info" size="small" />
          </Box>

          {/* Last Brightpearl error — cleared only after a successful sync step. */}
          { !!application.sync_error &&
            <Alert severity="error" sx={{ mt: 2 }}>
              <AlertTitle>Error al sincronizar con Brightpearl</AlertTitle>
              <Typography variant="body2" sx={{ wordBreak: "break-word" }}>{ application.sync_error }</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                El proceso se reintenta automáticamente. Corrija la causa en Brightpearl y este mensaje desaparecerá cuando la sincronización se complete correctamente.
              </Typography>
            </Alert>
          }

          <Grid container spacing={1} alignItems="center" sx={{ mt: 2 }}>
            <Field label="Nombre">{ application.name }</Field>
            <Field label="Correo Electrónico">{ application.email }</Field>

            <Field label="Teléfono">
              { props.editable
                ? <AppInput name="phone" value={ values.phone } onChange={ formik.handleChange } error={ formik.errors.phone } />
                : application.phone }
            </Field>

            <Field label="Nombre de empresa">{ application.company_name }</Field>
            <Field label="Cédula jurídica">{ application.national_id }</Field>

            { !!application.economic_activity_code &&
              <Field label="Código de actividad económica">{ application.economic_activity_code }</Field>
            }

            <Field label="Departamento">{ temporalTranslations.companyDepartmentHuman }</Field>

            {/* Location: editable dropdowns or plain values */}
            { props.editable
              ? (
                <>
                  <Grid item xs={12}>
                    <LocationDropdowns
                      onChange={ formik.setFieldValue }
                      errors={ formik.errors }
                      locationsDataset={ props.locationsDataset }
                      initialValues={{
                        province: values.province,
                        canton: values.canton,
                        district: values.district,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}><Divider /></Grid>
                </>
              )
              : (
                <>
                  <Field label="Provincia">{ application.province }</Field>
                  <Field label="Cantón">{ application.canton }</Field>
                  <Field label="Distrito">{ application.district }</Field>
                </>
              )
            }

            <Field label="Zip">{ values.zip }</Field>

            <Field label="Direccion de entrega">
              { props.editable
                ? <AppInput name="address" value={ values.address } onChange={ formik.handleChange } error={ formik.errors.address } />
                : application.address }
            </Field>

            <Field label="Sector">{ temporalTranslations.sectorHuman }</Field>
            <Field label="Cantidad de empleados">{ temporalTranslations.employeesCountHuman }</Field>

            {/* Only editable before approving/rejecting */}
            <Field label="Asignar a">
              { props.pending
                ? (
                  <TextField
                    select fullWidth size="small"
                    name="brightpearl_contact_owner_id"
                    value={ values.brightpearl_contact_owner_id || "" }
                    onChange={ formik.handleChange }
                  >
                    { props.contact_owners.map( (option, index) =>
                      <MenuItem key={ option.value } value={ option.value }>{ option.label }</MenuItem>
                    )}
                  </TextField>
                )
                : props.contact_owner }
            </Field>

            <Field label="Canal por el cual conoció a Unimart Business">
              <TextField
                select fullWidth size="small"
                name="referral"
                value={ values.referral || "" }
                onChange={ formik.handleChange }
              >
                { props.temporalTranslations.referralOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
            </Field>

            {/* Shopify and Brightpearl indicators */}
            { application.status == "accepted" &&
              <>
                <Field label="Shopify ID">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip label={ shopify_published ? "Creado" : "Pendiente" } color={ shopify_published ? "success" : "default" } size="small" />
                    { shopify_published &&
                      <IconButton component="a" target="_blank" rel="noopener noreferrer" href={ props.shopifyURL } size="small" color="primary">
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    }
                  </Box>
                </Field>
                <Field label="Brightpearl ID">
                  <Chip label={ bp_published ? "Creado" : "Pendiente" } color={ bp_published ? "success" : "default" } size="small" />
                </Field>
              </>
            }
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 2.5 }}>
            {/* Save form changes */}
            { props.editable &&
              <Button type="button" onClick={ formik.handleSubmit } variant="contained" color="info" disableElevation>Guardar</Button>
            }

            {/* Approve/Reject */}
            { props.pending &&
              <>
                <form action={ props.approveURL } method="POST">
                  <input type="hidden" name="authenticity_token" value={ csrfToken } />
                  <Button type="submit" data-confirm="¿Está seguro que desea aprobar la solicitud?" variant="contained" color="primary" disableElevation>Aprobar</Button>
                </form>

                <form action={ props.rejectURL } method="POST">
                  <input type="hidden" name="authenticity_token" value={ csrfToken } />
                  <Button type="submit" data-confirm="¿Está seguro que desea rechazar la solicitud?" variant="contained" color="error" disableElevation>Rechazar</Button>
                </form>
              </>
            }
          </Box>
        </CardContent>
      </Card>
    </Container>
  )
};

BusinessApplicationShow.propTypes = propTypes;
BusinessApplicationShow.defaultProps = defaultProps;

export default BusinessApplicationShow;
